import {
  Checkbox,
  ContextMenu,
  ContextMenuItem,
  Button,
  Typography,
} from "@/components/ui";
import type { NotificationInboxItem } from "@/lib/data/requests";
import { getNotificationVisual } from "@/components/ui/NotificationItems/notificationVisual";
import { getSmsReplyTarget } from "@/components/ui/NotificationItems/notificationCategories";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { queryKeys } from "@/lib/data/static";
import { cn } from "@/lib/utils/cn";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  ExternalLink,
  Mail,
  MailOpen,
  MessageSquareReply,
  Trash2,
} from "lucide-react";
import type { KeyboardEvent } from "react";

dayjs.extend(relativeTime);

const asUtcTimestamp = (value: string) =>
  /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`;

export const NotificationItem = ({
  notification,
  onSelect,
  onNotificationClick,
  onContextMenu,
  onContextMenuOpenChange,
  onUpdateSelectedReadState,
  onDelete,
  canMarkRead = false,
  canMarkUnread = false,
  isUpdatingSelectedReadState = false,
  selectedNotificationCount = 0,
  isSelected = false,
  onToggleSelect,
  matterTitle,
}: {
  notification: NotificationInboxItem;
  onSelect?: () => void;
  onNotificationClick?: () => void;
  onContextMenu?: () => void;
  onContextMenuOpenChange?: (open: boolean) => void;
  onUpdateSelectedReadState?: (read: boolean) => void;
  onDelete?: () => void;
  canMarkRead?: boolean;
  canMarkUnread?: boolean;
  isUpdatingSelectedReadState?: boolean;
  selectedNotificationCount?: number;
  isSelected?: boolean;
  onToggleSelect?: (notificationId: string, shiftKey: boolean) => void;
  matterTitle?: string;
}) => {
  const notificationId = notification.id;
  const matterId = notification.destination?.matterId;
  const matterUrl = matterId ? `/matters/${matterId}` : null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openCommunicationModal } = useGlobalModal();
  const { hasActionPermission } = useUser();
  const replyTarget = getSmsReplyTarget(notification);
  const canReply =
    Boolean(replyTarget) &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.contact,
      ACTION_PERMISSION_VALUES.create,
    );

  const handleReply = () => {
    if (!replyTarget) return;
    onNotificationClick?.();
    onSelect?.();
    openCommunicationModal(replyTarget);
  };

  const handleSelect = () => {
    onNotificationClick?.();
    onSelect?.();
    if (matterId && matterUrl) {
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, matterId],
      });
      void navigate({ to: matterUrl });
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelect();
    }
  };

  const visual = getNotificationVisual(notification.eventType);
  const Icon = visual.Icon;

  const card = (
    <div className="flex items-start gap-3 border-b px-5 py-5">
      {onToggleSelect ? (
        <div
          className="group/notification-checkbox -m-2 flex h-[34px] w-[34px] shrink-0 items-center justify-center self-center"
          onClick={(event) => {
            event.stopPropagation();
            onToggleSelect(notificationId, event.shiftKey);
          }}
        >
          <Checkbox
            checked={isSelected}
            aria-label={
              isSelected ? "Deselect notification" : "Select notification"
            }
            className={cn(
              "h-[18px] w-[18px] shrink-0 border-slate-300 opacity-0 transition-[opacity,border-color,background-color] group-hover:opacity-100 group-hover/notification-checkbox:border-blue-500 data-[state=checked]:border-blue-500 data-[state=checked]:bg-blue-500 data-[state=checked]:text-white",
              isSelected && "opacity-100",
            )}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
          />
        </div>
      ) : null}
      <div
        className={cn(
          "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl",
          visual.className,
        )}
      >
        <Icon className="h-6 w-6" />
      </div>
      <div className="min-w-0 flex-1">
        <Typography
          variant="default"
          size="md"
          weight="semibold"
          className="text-foreground/80 truncate leading-tight"
        >
          {notification.title}
        </Typography>
        {matterId && notification.body.preview?.trim() ? (
          <Typography
            variant="subHeading"
            size="sm"
            className="text-muted-foreground mt-1 line-clamp-2 break-words"
          >
            {notification.body.preview}
          </Typography>
        ) : null}
        {matterId && notification.body.details.length > 0 ? (
          <dl className="text-muted-foreground mt-1 grid min-w-0 gap-y-0.5 text-xs">
            {notification.body.details.map((detail, index) => (
              <div
                key={`${detail.label}-${index}`}
                className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-1"
              >
                <dt className="font-medium">{detail.label}:</dt>
                <dd className="truncate" title={detail.value}>
                  {detail.value}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
        <div className="text-muted-foreground mt-1 flex min-w-0 items-center gap-2 text-sm">
          {matterId ? (
            <>
              <span className="truncate">{matterTitle ?? "Matter"}</span>
              <span aria-hidden className="shrink-0">
                ·
              </span>
            </>
          ) : null}
          <span className="shrink-0 whitespace-nowrap">
            {dayjs(asUtcTimestamp(notification.dateCreated)).fromNow()}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 self-center">
        {canReply ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              handleReply();
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
          >
            <MessageSquareReply className="size-4" /> Reply
          </Button>
        ) : null}
        {!notification.readAt ? (
          <span className="bg-primary h-3 w-3 shrink-0 rounded-full" />
        ) : null}
      </div>
    </div>
  );

  const content = (
    <div
      className={cn(
        "transition-colors",
        "group hover:bg-muted/40",
        matterUrl && "cursor-pointer",
        !notification.readAt && "bg-primary/5 hover:bg-primary/10",
        isSelected &&
          (notification.readAt
            ? "bg-muted/70 hover:bg-muted/70"
            : "bg-primary/15 hover:bg-primary/15"),
      )}
    >
      {card}
    </div>
  );

  return (
    <ContextMenu
      entityName="notification"
      onContextMenu={onContextMenu}
      onOpenChange={onContextMenuOpenChange}
      trigger={
        <div
          role="button"
          tabIndex={0}
          onClick={handleSelect}
          onKeyDown={handleKeyDown}
          className={cn(
            "rounded-lg",
            matterUrl && "cursor-pointer",
            "focus-visible:ring-primary focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden",
          )}
        >
          {content}
        </div>
      }
    >
      {selectedNotificationCount <= 1 && canReply ? (
        <ContextMenuItem onSelect={handleReply}>
          <MessageSquareReply />
          Reply
        </ContextMenuItem>
      ) : null}
      {selectedNotificationCount <= 1 && matterUrl ? (
        <ContextMenuItem
          onSelect={() => {
            window.open(matterUrl, "_blank", "noopener,noreferrer");
          }}
        >
          <ExternalLink />
          Open matter in new tab
        </ContextMenuItem>
      ) : null}
      <ContextMenuItem
        disabled={isUpdatingSelectedReadState || !canMarkRead}
        onSelect={() => {
          onUpdateSelectedReadState?.(true);
        }}
      >
        <MailOpen />
        {selectedNotificationCount > 1
          ? "Mark selected notifications as read"
          : "Mark as read"}
      </ContextMenuItem>
      <ContextMenuItem
        disabled={isUpdatingSelectedReadState || !canMarkUnread}
        onSelect={() => {
          onUpdateSelectedReadState?.(false);
        }}
      >
        <Mail />
        {selectedNotificationCount > 1
          ? "Mark selected notifications as unread"
          : "Mark as unread"}
      </ContextMenuItem>
      {selectedNotificationCount <= 1 && onDelete ? (
        <ContextMenuItem onSelect={onDelete}>
          <Trash2 />
          Delete notification
        </ContextMenuItem>
      ) : null}
    </ContextMenu>
  );
};
